import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useIsDarkMode } from '../../hooks/useDarkMode';

type BlobKind = 'teal' | 'teal-2' | 'amber' | 'indigo';

type BlobConfig = {
  kind: BlobKind;
  style: CSSProperties;
};

function rand(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function wanderPoint() {
  return {
    x: `${rand(-32, 34).toFixed(1)}vw`,
    y: `${rand(-28, 40).toFixed(1)}vh`,
    scale: rand(0.82, 1.32).toFixed(2),
    opacity: rand(0.42, 0.92).toFixed(2),
  };
}

function createBlobConfig(kind: BlobKind, dark: boolean): BlobConfig {
  const horizontal = Math.random() > 0.5 ? 'left' : 'right';
  const vertical = Math.random() > 0.5 ? 'top' : 'bottom';
  const anchor = {
    [horizontal]: `${rand(-24, 18).toFixed(1)}%`,
    [vertical]: `${rand(-16, 32).toFixed(1)}%`,
  };

  const p1 = wanderPoint();
  const p2 = wanderPoint();
  const p3 = wanderPoint();
  const p4 = wanderPoint();

  return {
    kind,
    style: {
      ...anchor,
      animationDuration: `${rand(14, 27).toFixed(1)}s`,
      animationDelay: `${rand(-18, 0).toFixed(1)}s`,
      ['--bx1' as string]: p1.x,
      ['--by1' as string]: p1.y,
      ['--bs1' as string]: p1.scale,
      ['--bo1' as string]: dark ? `${Number(p1.opacity) * 0.9}` : p1.opacity,
      ['--bx2' as string]: p2.x,
      ['--by2' as string]: p2.y,
      ['--bs2' as string]: p2.scale,
      ['--bo2' as string]: dark ? `${Number(p2.opacity) * 0.9}` : p2.opacity,
      ['--bx3' as string]: p3.x,
      ['--by3' as string]: p3.y,
      ['--bs3' as string]: p3.scale,
      ['--bo3' as string]: dark ? `${Number(p3.opacity) * 0.9}` : p3.opacity,
      ['--bx4' as string]: p4.x,
      ['--by4' as string]: p4.y,
      ['--bs4' as string]: p4.scale,
      ['--bo4' as string]: dark ? `${Number(p4.opacity) * 0.9}` : p4.opacity,
    },
  };
}

export function RoomBackground() {
  const dark = useIsDarkMode();
  const blobs = useMemo(
    () =>
      (['teal', 'teal-2', 'amber', 'indigo'] as const).map(kind => createBlobConfig(kind, dark)),
    [dark]
  );

  return (
    <div className="room-bg" aria-hidden>
      <div className="room-bg__base" />
      {blobs.map(blob => (
        <div
          key={blob.kind}
          className={`room-bg__blob room-bg__blob--${blob.kind}`}
          style={blob.style}
        />
      ))}
    </div>
  );
}
